import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  CircularProgress,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Message as MessageIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Visibility as ViewIcon,
  Send as SendIcon,
  AccountBalance as BalanceIcon,
  List as ListIcon,
  Add as AddIcon,
  Assessment as ReportIcon,
  Phone as PhoneIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import api from '../services/api';
import SmsStatus from './SmsStatus';

const SmsManagement = () => {
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [smsDetailsDialog, setSmsDetailsDialog] = useState(false);
  const [resendDialog, setResendDialog] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  
  // New state for enhanced features
  const [activeTab, setActiveTab] = useState(0);
  const [balance, setBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [deliveryReportDialog, setDeliveryReportDialog] = useState(false);
  const [deliveryReport, setDeliveryReport] = useState(null);
  const [deliveryReportLoading, setDeliveryReportLoading] = useState(false);
  const [deliveryForm, setDeliveryForm] = useState({
    msgId: ''
  });

  useEffect(() => {
    fetchComplaintsWithSmsStatus();
    if (activeTab === 1) {
      fetchBalance();
    }
  }, [activeTab]);

  const fetchComplaintsWithSmsStatus = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/complaints/sms-status/all');
      setComplaints(response.data);
    } catch (error) {
      console.error('Error fetching complaints with SMS status:', error);
      
      // If it's a network error (CORS, 525, etc.), try fallback endpoint
      if (error.code === 'ERR_NETWORK' || !error.response) {
        console.warn('Network error detected, trying fallback endpoint');
        try {
          const fallbackResponse = await api.get('/complaints');
          setComplaints(fallbackResponse.data);
          setError('SMS status endpoint unavailable. Showing complaints without detailed SMS status.');
        } catch (fallbackError) {
          console.error('Error fetching complaints (fallback):', fallbackError);
          setError('Failed to fetch complaints data. Please check your connection and try again.');
        }
      } else {
        setError('Failed to fetch SMS status data');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchBalance = async () => {
    setBalanceLoading(true);
    try {
      const response = await api.get('/complaints/sms/balance');
      if (response.data.success) {
        // Handle different balance response formats
        const balanceData = response.data.balance;
        if (typeof balanceData === 'string' || typeof balanceData === 'number') {
          setBalance(balanceData.toString());
        } else if (balanceData && typeof balanceData === 'object') {
          // If it's an object, try to extract balance info
          setBalance(JSON.stringify(balanceData));
        } else {
          setBalance('N/A');
        }
      } else {
        setError('Failed to fetch balance');
        setBalance('N/A');
      }
    } catch (error) {
      console.error('Error fetching balance:', error);
      setError('Failed to fetch balance');
      setBalance('N/A');
    } finally {
      setBalanceLoading(false);
    }
  };

  const handleGetDeliveryReport = async () => {
    if (!deliveryForm.msgId) {
      setError('Please provide Message ID');
      return;
    }

    setDeliveryReportLoading(true);
    try {
      const response = await api.get('/complaints/sms/delivery-report', {
        params: { msgId: deliveryForm.msgId }
      });
      
      if (response.data.success) {
        setDeliveryReport(response.data.report);
        setDeliveryReportDialog(true);
      } else {
        setError(response.data.message || 'Failed to get delivery report');
      }
    } catch (error) {
      console.error('Error getting delivery report:', error);
      setError(error.response?.data?.message || 'Failed to get delivery report');
    } finally {
      setDeliveryReportLoading(false);
    }
  };

  const handleResendSms = async (complaintId, smsType) => {
    setResendLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await api.post(`/complaints/${complaintId}/resend-sms`, {
        smsType: smsType
      });
      
      setSuccess(`SMS resent successfully for complaint ${complaintId}!`);
      setResendDialog(false);
      
      // Refresh data
      await fetchComplaintsWithSmsStatus();
      
      // Update selected complaint data if it's the same complaint
      if (selectedComplaint && selectedComplaint.complaintId === complaintId) {
        const updatedComplaints = await api.get('/complaints/sms-status/all');
        const updatedComplaint = updatedComplaints.data.find(c => c.complaintId === complaintId);
        if (updatedComplaint) {
          setSelectedComplaint(updatedComplaint);
        }
      }
    } catch (error) {
      console.error('Error resending SMS:', error);
      setError(error.response?.data?.error || 'Failed to resend SMS');
    } finally {
      setResendLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent':
        return 'success';
      case 'failed':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return <CheckCircleIcon />;
      case 'failed':
        return <ErrorIcon />;
      case 'pending':
        return <ScheduleIcon />;
      default:
        return <MessageIcon />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not sent';
    return new Date(dateString).toLocaleString();
  };

  // Filter complaints based on search and status
  const filteredComplaints = complaints.filter(complaint => {
    const matchesSearch = 
      complaint.complaintId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      complaint.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      complaint.mobileNumber?.includes(searchTerm);
    
    const matchesStatus = statusFilter === 'all' || 
      complaint.smsStatus === statusFilter ||
      complaint.statusUpdateSmsStatus === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Pagination
  const totalPages = Math.ceil(filteredComplaints.length / rowsPerPage);
  const paginatedComplaints = filteredComplaints.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const openSmsDetails = async (complaint) => {
    // Refresh the complaint data before opening the dialog
    try {
      const response = await api.get('/complaints/sms-status/all');
      const updatedComplaint = response.data.find(c => c.complaintId === complaint.complaintId);
      if (updatedComplaint) {
        setSelectedComplaint(updatedComplaint);
      } else {
        setSelectedComplaint(complaint);
      }
    } catch (error) {
      console.error('Error refreshing complaint data:', error);
      setSelectedComplaint(complaint);
    }
    setSmsDetailsDialog(true);
  };

  const refreshSmsDetails = async () => {
    if (selectedComplaint) {
      setRefreshLoading(true);
      try {
        // Fetch the latest SMS status for this specific complaint
        const smsResponse = await api.get(`/complaints/${selectedComplaint.complaintId}/sms-status`);
        if (smsResponse.data && smsResponse.data.success) {
          // Update the selected complaint with the latest SMS status
          setSelectedComplaint(prev => ({
            ...prev,
            smsStatus: smsResponse.data.data.confirmation?.status || 'not_attempted',
            smsSentAt: smsResponse.data.data.confirmation?.sentAt,
            smsError: smsResponse.data.data.confirmation?.error,
            statusUpdateSmsStatus: smsResponse.data.data.statusUpdate?.status || 'not_attempted',
            statusUpdateSmsSentAt: smsResponse.data.data.statusUpdate?.sentAt,
            statusUpdateSmsError: smsResponse.data.data.statusUpdate?.error
          }));
        }
        
        // Also refresh the main complaints list
        try {
          const response = await api.get('/complaints/sms-status/all');
          const updatedComplaint = response.data.find(c => c.complaintId === selectedComplaint.complaintId);
          if (updatedComplaint) {
            setSelectedComplaint(updatedComplaint);
          }
        } catch (refreshError) {
          console.warn('Could not refresh complaints list, using individual SMS status only:', refreshError.message);
          // Continue with the individual SMS status we already fetched
        }
      } catch (error) {
        console.error('Error refreshing SMS details:', error);
      } finally {
        setRefreshLoading(false);
      }
    }
  };

  const getSmsStatusSummary = (complaint) => {
    const confirmationStatus = complaint.smsStatus || 'not_attempted';
    const statusUpdateStatus = complaint.statusUpdateSmsStatus || 'not_attempted';
    
    if (confirmationStatus === 'failed' || statusUpdateStatus === 'failed') {
      return { status: 'failed', count: 1 };
    } else if (confirmationStatus === 'sent' && statusUpdateStatus === 'sent') {
      return { status: 'sent', count: 2 };
    } else if (confirmationStatus === 'sent' || statusUpdateStatus === 'sent') {
      return { status: 'partial', count: 1 };
    } else {
      return { status: 'not_attempted', count: 0 };
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (loading && activeTab === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MessageIcon />
          SMS Management
        </Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={activeTab === 0 ? fetchComplaintsWithSmsStatus : () => {
            fetchBalance();
          }}
          disabled={loading || balanceLoading}
          variant="outlined"
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="sms management tabs">
          <Tab label="SMS Status" />
          <Tab label="SMS Configuration" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && (
        <>
          {/* Filters */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Search complaints"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Status Filter</InputLabel>
                    <Select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      label="Status Filter"
                    >
                      <MenuItem value="all">All Statuses</MenuItem>
                      <MenuItem value="sent">Sent</MenuItem>
                      <MenuItem value="failed">Failed</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="not_attempted">Not Attempted</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Total: {filteredComplaints.length} complaints
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* SMS Status Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Complaint ID</strong></TableCell>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Mobile</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>SMS Summary</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedComplaints.map((complaint) => {
                  const summary = getSmsStatusSummary(complaint);
                  return (
                    <TableRow key={complaint._id}>
                      <TableCell>{complaint.complaintId}</TableCell>
                      <TableCell>{complaint.name}</TableCell>
                      <TableCell>{complaint.mobileNumber}</TableCell>
                      <TableCell>
                        <Chip
                          label={complaint.status}
                          size="small"
                          color={complaint.status === 'Resolved' ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Chip
                            icon={getStatusIcon(complaint.smsStatus)}
                            label={`Conf: ${complaint.smsStatus || 'N/A'}`}
                            color={getStatusColor(complaint.smsStatus)}
                            size="small"
                          />
                          <Chip
                            icon={getStatusIcon(complaint.statusUpdateSmsStatus)}
                            label={`Update: ${complaint.statusUpdateSmsStatus || 'N/A'}`}
                            color={getStatusColor(complaint.statusUpdateSmsStatus)}
                            size="small"
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="View SMS Details">
                            <IconButton
                              size="small"
                              onClick={() => openSmsDetails(complaint)}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          {(complaint.smsStatus === 'failed' || complaint.statusUpdateSmsStatus === 'failed') && (
                            <Tooltip title="Resend SMS">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => {
                                  setSelectedComplaint(complaint);
                                  setResendDialog(true);
                                }}
                              >
                                <SendIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {activeTab === 1 && (
        <Grid container spacing={3}>
          {/* Balance Card */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <BalanceIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">SMS Balance</Typography>
                </Box>
                {balanceLoading ? (
                  <CircularProgress size={20} />
                ) : balance ? (
                  <Typography variant="h4" color="primary">
                    {balance}
                  </Typography>
                ) : (
                  <Typography color="text.secondary">No balance info available</Typography>
                )}
                <Button
                  startIcon={<RefreshIcon />}
                  onClick={fetchBalance}
                  disabled={balanceLoading}
                  sx={{ mt: 2 }}
                  size="small"
                >
                  Refresh Balance
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Sender IDs Card */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ListIcon sx={{ mr: 1 }} />
                    <Typography variant="h6">SMS Configuration</Typography>
                  </Box>
                </Box>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  Using BulkSMS API for SMS delivery. Sender ID management is handled through the BulkSMS dashboard.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>API:</strong> BulkSMS Service
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Route:</strong> Transactional (Route 2)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Features:</strong> Balance Check, Delivery Reports, Scheduling
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Delivery Report Card */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ReportIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">Delivery Report</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Message ID"
                      value={deliveryForm.msgId}
                      onChange={(e) => setDeliveryForm({...deliveryForm, msgId: e.target.value})}
                      size="small"
                      placeholder="Enter message ID from SMS response"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Button
                      fullWidth
                      startIcon={<ReceiptIcon />}
                      onClick={handleGetDeliveryReport}
                      disabled={deliveryReportLoading}
                      variant="contained"
                    >
                      Get Report
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* SMS Details Dialog */}
      <Dialog 
        open={smsDetailsDialog} 
        onClose={() => setSmsDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          SMS Details - {selectedComplaint?.complaintId}
          <Button
            startIcon={refreshLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={refreshSmsDetails}
            disabled={refreshLoading}
            size="small"
            sx={{ ml: 2 }}
          >
            {refreshLoading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </DialogTitle>
        <DialogContent>
          {selectedComplaint && (
            <SmsStatus 
              key={selectedComplaint.complaintId} // Force re-render when complaint changes
              complaintId={selectedComplaint.complaintId}
              onRefresh={refreshSmsDetails}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSmsDetailsDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resend Dialog */}
      <Dialog open={resendDialog} onClose={() => setResendDialog(false)}>
        <DialogTitle>Resend SMS</DialogTitle>
        <DialogContent>
          <Typography>
            Which SMS would you like to resend for complaint {selectedComplaint?.complaintId}?
          </Typography>
          <Box sx={{ mt: 2 }}>
            {selectedComplaint?.smsStatus === 'failed' && (
              <Button
                fullWidth
                variant="outlined"
                onClick={() => handleResendSms(selectedComplaint.complaintId, 'confirmation')}
                disabled={resendLoading}
                sx={{ mb: 1 }}
              >
                Resend Confirmation SMS
              </Button>
            )}
            {selectedComplaint?.statusUpdateSmsStatus === 'failed' && (
              <Button
                fullWidth
                variant="outlined"
                onClick={() => handleResendSms(selectedComplaint.complaintId, 'status_update')}
                disabled={resendLoading}
              >
                Resend Status Update SMS
              </Button>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResendDialog(false)} disabled={resendLoading}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Request Sender ID Dialog */}
      {/* Removed Request Sender ID Dialog */}

      {/* Delivery Report Dialog */}
      <Dialog 
        open={deliveryReportDialog} 
        onClose={() => setDeliveryReportDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Delivery Report</DialogTitle>
        <DialogContent>
          {deliveryReport ? (
            <Box>
              <Typography variant="h6" gutterBottom>Report Details:</Typography>
              <pre style={{ 
                backgroundColor: '#f5f5f5', 
                padding: '16px', 
                borderRadius: '4px',
                overflow: 'auto',
                maxHeight: '400px'
              }}>
                {JSON.stringify(deliveryReport, null, 2)}
              </pre>
            </Box>
          ) : (
            <Typography>No report data available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeliveryReportDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SmsManagement; 